import React, { useContext, useState, useEffect } from 'react';
import { AiOutlineClose, AiOutlineUser } from 'react-icons/ai';
import { MdOutlineSettings } from 'react-icons/md';
import { AuthContext } from '../../context/AuthContext';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { toast } from '../../utils/toast';
import { updateUserProfile } from '../../store/slices/authSlice';

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const SettingsModal = ({ isOpen, onClose }: SettingsModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { isAuth } = useContext(AuthContext);
    const reduxUser = useSelector((state: RootState) => state.auth.user);
    const [isEditing, setIsEditing] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const fallbackUser = {
        firstname: "Gautam",
        lastname: "Budh",
        email: "gautambudh@gmail.com",
        phone: "+91 9876543210"
    };

    // Build userData from Redux user if logged in, else use fallback
    const userData = isAuth && reduxUser ? {
        firstname: reduxUser.first_name || reduxUser.firstname || fallbackUser.firstname,
        lastname: reduxUser.last_name || reduxUser.lastname || fallbackUser.lastname,
        email: reduxUser.email || reduxUser.username || fallbackUser.email,
        phone: reduxUser.phone_number || fallbackUser.phone
    } : fallbackUser;

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const actionResult = await dispatch(updateUserProfile({
                first_name: firstName,
                last_name: lastName,
                email: email
            }));

            if (updateUserProfile.fulfilled.match(actionResult)) {
                toast.success('Profile updated successfully!');
                setIsEditing(false);
            } else {
                toast.error(actionResult.payload as string || 'Failed to update profile');
            }
        } catch (err: any) {
            toast.error(err.message || 'An error occurred');
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="settings-modal-overlay" onClick={onClose}>
            <style>
                {`
                .settings-modal-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    display: flex;
                    justify-content: flex-end;
                    background-color: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    animation: fadeInOverlay 0.3s ease;
                }

                @keyframes fadeInOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideInRight {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }

                .settings-drawer {
                    animation: slideInRight 0.45s cubic-bezier(0.16, 1, 0.3, 1);
                    width: 32% !important;
                    min-width: 440px !important;
                    background-color: #ffffff;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    box-shadow: -15px 0 45px rgba(15, 23, 42, 0.08);
                    border-top-left-radius: 24px;
                    border-bottom-left-radius: 24px;
                    overflow: hidden;
                }

                .settings-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 24px;
                    border-bottom: 1px solid #f3f4f6;
                    background-color: #ffffff;
                }

                .settings-title-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .settings-title {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 800;
                    color: #1f2937;
                    letter-spacing: -0.5px;
                }

                .settings-close-btn {
                    background: #f3f4f6;
                    border: none;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #4b5563;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .settings-close-btn:hover {
                    background-color: #e5e7eb;
                    color: #111827;
                    transform: scale(1.05);
                }

                .settings-content {
                    padding: 24px;
                    overflow-y: auto;
                    flex: 1;
                    background-color: #fcfcfc;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .settings-section-card {
                    background: #ffffff;
                    border: 1px solid #f3f4f6;
                    padding: 20px;
                    border-radius: 16px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.01);
                }

                .settings-section-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid #f3f4f6;
                    padding-bottom: 12px;
                    margin-bottom: 16px;
                }

                .settings-section-title {
                    font-size: 11.5px;
                    font-weight: 800;
                    color: #9ca3af;
                    margin: 0;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                }

                .profile-summary-row {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 20px;
                }

                .avatar-badge {
                    width: 56px;
                    height: 56px;
                    border-radius: 14px;
                    background-color: #43c8ca;
                    color: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    font-weight: 800;
                    text-transform: uppercase;
                    box-shadow: 0 4px 10px rgba(67, 200, 202, 0.2);
                }

                .settings-grid {
                    display: flex;
                    flex-direction: column;
                    gap: 14px;
                }

                .settings-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                    text-align: left;
                }

                .settings-info-label {
                    font-size: 11.5px;
                    font-weight: 850;
                    color: #9ca3af;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .settings-info-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1f2937;
                    word-break: break-all;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    text-align: left;
                }

                .form-label {
                    font-size: 11.5px;
                    font-weight: 800;
                    color: #4b5563;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .form-input {
                    width: 100%;
                    padding: 12px 16px;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    outline: none;
                    font-size: 14px;
                    color: #1f2937;
                    background-color: #f9fafb;
                    transition: all 0.2s;
                    box-sizing: border-box;
                }

                .form-input:focus {
                    border-color: #43c8ca;
                    background-color: #ffffff;
                    box-shadow: 0 0 0 3px rgba(67, 200, 202, 0.15);
                }

                .primary-action-btn {
                    width: 100%;
                    padding: 14px;
                    background-color: #43c8ca;
                    color: #ffffff;
                    font-weight: 800;
                    border: none;
                    border-radius: 12px;
                    cursor: pointer;
                    font-size: 13.5px;
                    letter-spacing: 0.8px;
                    text-transform: uppercase;
                    box-shadow: 0 4px 12px rgba(67, 200, 202, 0.25);
                    transition: all 0.2s;
                }

                .primary-action-btn:hover:not(:disabled) {
                    background-color: #3db0b2;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px rgba(67, 200, 202, 0.35);
                }

                .primary-action-btn:disabled {
                    background-color: #d1d5db;
                    color: #9ca3af;
                    box-shadow: none;
                    cursor: not-allowed;
                }

                @media (max-width: 768px) {
                    .settings-drawer {
                        width: 100% !important;
                        min-width: unset !important;
                        border-radius: 0;
                    }
                    .settings-header {
                        padding: 16px 20px !important;
                    }
                    .settings-content {
                        padding: 16px !important;
                    }
                }
                `}
            </style>

            <div className="settings-drawer" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="settings-header">
                    <div className="settings-title-container">
                        <MdOutlineSettings size={22} color="#43c8ca" />
                        <h2 className="settings-title">Settings</h2>
                    </div>
                    <button onClick={onClose} className="settings-close-btn" aria-label="Close settings">
                        <AiOutlineClose size={18} />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="settings-content">
                    {/* Profile section */}
                    <div className="settings-section-card">
                        <div className="settings-section-header">
                            <h3 className="settings-section-title">Personal Info</h3>
                            {isAuth && (
                                <button
                                    onClick={() => {
                                        if (!isEditing) {
                                            setFirstName(userData.firstname);
                                            setLastName(userData.lastname || '');
                                            setEmail(userData.email);
                                        }
                                        setIsEditing(!isEditing);
                                    }}
                                    style={{
                                        background: 'none', border: 'none', color: '#43c8ca',
                                        cursor: 'pointer', fontSize: '11.5px', fontWeight: '800',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}
                                >
                                    {isEditing ? 'Cancel' : 'Edit'}
                                </button>
                            )}
                        </div>

                        {!isEditing ? (
                            <>
                                <div className="profile-summary-row">
                                    <div className="avatar-badge">
                                        {userData.firstname ? userData.firstname[0].toUpperCase() : 'U'}
                                    </div>
                                    <div style={{ textAlign: 'left' }}>
                                        <div style={{ fontSize: '16.5px', fontWeight: 800, color: '#1f2937', textTransform: 'capitalize' }}>
                                            {userData.firstname} {userData.lastname || ''}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px', fontWeight: 500 }}>
                                            Account Status: <span style={{ color: '#43c8ca', fontWeight: 700 }}>Active</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="settings-grid">
                                    <div className="settings-info-item">
                                        <span className="settings-info-label">Email</span>
                                        <span className="settings-info-value">{userData.email}</span>
                                    </div>
                                    <div className="settings-info-item">
                                        <span className="settings-info-label">Phone</span>
                                        <span className="settings-info-value">{userData.phone}</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div className="form-group">
                                    <label className="form-label">First Name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={firstName}
                                        onChange={e => setFirstName(e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Last Name</label>
                                    <input
                                        type="text"
                                        value={lastName}
                                        onChange={e => setLastName(e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Email *</label>
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                                <button type="submit" disabled={isSaving} className="primary-action-btn" style={{ marginTop: '8px' }}>
                                    {isSaving ? 'Saving...' : 'Save Profile'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsModal;
